import { Banner, Box, Button, Spinner, TextField } from "@modernatx-internal/component-library";
import React from "react";

import { useTextBlockRendering } from "@/components/TextBlock/TextBlockRenderContext";
import { ContentComponentProps, SimpleNewsletterFormContent } from "@/content-V2/types";
import { useExperience } from "@/context/ExperienceContext";
import { useNewsletterForm } from "@/hooks/useNewsletterForm";

export const SimpleNewsletterForm: React.FC<ContentComponentProps<SimpleNewsletterFormContent>> = ({
  buttonText,
  errorText,
  labelText,
  leadContext = {},
  successText,
  validationText
}) => {
  const { editing } = useTextBlockRendering();
  const { expandedMode } = useExperience();
  const { error, loading, submitForm, success } = useNewsletterForm();

  const [email, emailSet] = React.useState("");
  const emailValid = !!email.trim().length && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  const [validationErrorVisible, validationErrorVisibleSet] = React.useState(false);

  const handleSubmit = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      if (!editing && emailValid) {
        submitForm({
          ...leadContext,
          email
        });
      }
    },
    [editing, emailValid, email, leadContext, submitForm]
  );

  const handleSubmitButtonClick = React.useCallback(() => {
    if (!emailValid) {
      validationErrorVisibleSet(true);
    }
  }, [emailValid]);

  return (
    <Box
      as="form"
      data-track="newsletter-form"
      data-track-form-validated={emailValid}
      sx={{ position: "relative" }}
      onSubmit={handleSubmit}
    >
      <Box
        sx={{
          opacity: loading ? 0.2 : 1,
          transition: "transition-color-slow"
        }}
      >
        {(expandedMode || (!emailValid && validationErrorVisible)) && (
          <Box sx={{ pb: 4 }}>
            <Banner variant="critical" text={validationText} />
          </Box>
        )}
        {(expandedMode || error) && (
          <Box sx={{ pb: 4 }}>
            <Banner variant="critical" text={errorText} />
          </Box>
        )}
        {(expandedMode || success) && (
          <Box sx={{ pb: 4 }}>
            <Banner variant="success" text={successText} />
          </Box>
        )}
        {(expandedMode || !success) && (
          <Box
            sx={{
              alignItems: "flex-start",
              display: "flex",
              flexDirection: ["column", null, "row"],
              gap: 2,
              justifyContent: "center",
              width: "100%"
            }}
          >
            <Box sx={{ color: "text-default", flex: 1, width: ["100%", null, "auto"] }}>
              <TextField
                label={labelText}
                maxLength={254}
                name="Email"
                required={true}
                value={email}
                onChange={(e) => emailSet(e.currentTarget.value)}
              />
            </Box>
            <Button
              brand="secondary"
              size="lg"
              sx={{ minWidth: 40 }}
              type="submit"
              onClick={handleSubmitButtonClick}
            >
              {buttonText}
            </Button>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          height: "100%",
          justifyContent: "center",
          left: 0,
          opacity: loading ? 0.8 : 0,
          pointerEvents: loading ? "auto" : "none",
          position: "absolute",
          top: 0,
          transition: "transition-color-slow",
          width: "100%"
        }}
      >
        <Spinner />
      </Box>
    </Box>
  );
};
